// SettingsTab — 设置 Tab
// 复刻 LocalSend settings_tab.dart：别名、主题、账号、关于

import { useSettingsStore } from '../../store/settingsStore';
import { useAuthStore } from '../../store/authStore';
import { APP_NAME, PROTOCOL_VERSION } from '@kuaichuan/shared';
import { Logo } from '../common/Logo';
import { useToastStore } from '../common/Toast';
import { logout } from '../../lib/api';

interface SettingsTabProps {
  onOpenAuth: () => void;
}

export function SettingsTab({ onOpenAuth }: SettingsTabProps) {
  const { alias, theme, setAlias, toggleTheme } = useSettingsStore();
  const { user, token, clear } = useAuthStore();
  const toast = useToastStore((s) => s.show);

  const handleLogout = async () => {
    if (token) await logout(token);
    clear();
    toast('已登出', 'success');
  };

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto pb-24 md:pb-8">
      <h2 className="text-xl font-bold text-ink mb-6">设置</h2>

      {/* 账号 */}
      <section className="card p-5 mb-4">
        <h3 className="text-sm font-medium text-muted mb-4">账号</h3>
        {user ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-full bg-primary-50 flex items-center justify-center text-xl shrink-0">
                {user.nickname?.[0] ?? user.email[0]?.toUpperCase() ?? '👤'}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-ink truncate">{user.nickname || user.email}</div>
                {user.nickname && <div className="text-xs text-muted truncate">{user.email}</div>}
              </div>
            </div>
            <button type="button" onClick={handleLogout} className="btn-outline shrink-0">
              登出
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm text-ink">未登录</div>
              <p className="text-xs text-muted mt-0.5">登录后开启同账号设备互连</p>
            </div>
            <button type="button" onClick={onOpenAuth} className="btn-primary shrink-0">
              登录 / 注册
            </button>
          </div>
        )}
      </section>

      {/* 常规 */}
      <section className="card p-5 mb-4">
        <h3 className="text-sm font-medium text-muted mb-4">常规</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-ink mb-1.5">设备别名</label>
            <input
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              className="input"
              placeholder="如：我的电脑"
              maxLength={32}
            />
            <p className="text-xs text-muted mt-1">其他设备会看到这个名字</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm text-ink">主题</label>
              <p className="text-xs text-muted mt-0.5">切换浅色/深色</p>
            </div>
            <button
              type="button"
              onClick={() => {
                toggleTheme();
                toast('主题已切换', 'success');
              }}
              className="btn-outline"
            >
              {theme === 'light' ? '☀️ 浅色' : '🌙 深色'}
            </button>
          </div>
        </div>
      </section>

      {/* 关于 */}
      <section className="card p-5">
        <h3 className="text-sm font-medium text-muted mb-4">关于</h3>
        <div className="flex items-center gap-3 mb-4">
          <Logo size="md" />
          <div>
            <div className="font-bold text-lg text-ink">{APP_NAME}</div>
            <div className="text-xs text-muted">极速跨设备点对点文件传输</div>
          </div>
        </div>
        <dl className="text-sm space-y-2">
          <div className="flex justify-between">
            <dt className="text-muted">协议版本</dt>
            <dd>v{PROTOCOL_VERSION}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted">技术栈</dt>
            <dd>React + Cloudflare Workers + WebRTC</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted">隐私</dt>
            <dd>文件直连传输，不经服务器</dd>
          </div>
        </dl>
      </section>

      <p className="text-center text-xs text-muted mt-6">
        快船 · 基于 LocalSend 协议重新实现
      </p>
    </div>
  );
}
